// /provision — the boot URL for unprovisioned signage devices, in particular
// the Raspberry Pi appliance image.
//
// Flow:
//   1. Page mounts. Calls /api/player/pair/start, gets a 6-char code.
//   2. Renders the code fullscreen, plus a tiny diagnostics line (so an
//      on-site tech can see hostname / agent / network).
//   3. Polls /api/player/pair/poll. When the admin claims the code from
//      the app, the response delivers a one-time raw token.
//   4. The page persists the token via the existing player-token-storage
//      helper and replaces the URL with /play/:screenId.
//
// Display contract:
//   * Pure black background, large white code, centered. Matches the
//     player chrome so the transition is visually seamless.
//   * No login, no auth provider, no React Router-aware UI: this page is
//     reachable from `/provision` outside of the AdminApp tree.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildPlayerUrl,
  completePairing,
  runPairing,
  type PairingHandle,
  type PairingState,
} from '@/lib/provision-client';
import { findStoredPlayerToken } from '@/lib/player-token-storage';

const PROVISIONED_FLAG_KEY = 'signage:provisioned-redirect';

export function ProvisionPage() {
  const [state, setState] = useState<PairingState>({
    status: 'starting',
    session: null,
    error: null,
    lastIssue: null,
    result: null,
    retryAtMs: null,
  });
  const handleRef = useRef<PairingHandle | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // Fire TV (and Amazon Signage Stick) relaunch the WebView at /provision on
  // every reboot. If we already have a player token from a previous pairing,
  // skip the code-display step and resume the player. The player route will
  // surface its own recovery UI if the stored token has been revoked.
  const [resumeTarget] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    if (new URLSearchParams(window.location.search).has('forcePair')) return null;
    const found = findStoredPlayerToken();
    return found ? `/play/${encodeURIComponent(found.screenId)}` : null;
  });

  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.body.classList.add('player-root');
    return () => {
      document.body.classList.remove('player-root');
    };
  }, []);

  // If a previous pairing's token is still on disk, hand off to the player
  // instead of firing a new pairing session.
  useEffect(() => {
    if (resumeTarget && typeof window !== 'undefined') {
      window.location.replace(resumeTarget);
    }
  }, [resumeTarget]);

  // Bind the pairing state machine.
  useEffect(() => {
    if (resumeTarget) return;
    const handle = runPairing({
      deviceLabel: deviceLabel(),
      deviceInfo: deviceInfo(),
      onState: setState,
    });
    handleRef.current = handle;
    return () => {
      void handle.stop();
      handleRef.current = null;
    };
  }, [resumeTarget]);

  // 1Hz tick so the countdown / "next attempt" line updates live.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // When pairing completes, persist the token and navigate to /play/:screenId.
  useEffect(() => {
    if (state.status !== 'completed' || !state.result) return;
    try {
      sessionStorage.setItem(PROVISIONED_FLAG_KEY, '1');
    } catch {
      /* ignore */
    }
    completePairing(state.result, { reload: true });
  }, [state.status, state.result]);

  const code = state.session?.code ?? '';
  const expiresAt = state.session?.expiresAt
    ? new Date(state.session.expiresAt).getTime()
    : null;
  const expiresIn = expiresAt ? Math.max(0, Math.round((expiresAt - now) / 1000)) : null;

  const previewUrl = useMemo(() => {
    if (!state.result) return null;
    return buildPlayerUrl(
      window.location.origin,
      state.result.screenId,
      state.result.token,
    );
  }, [state.result]);

  const onRestart = useCallback(() => {
    if (handleRef.current) void handleRef.current.restart();
  }, []);

  if (resumeTarget) {
    return (
      <div className="player-root flex flex-col items-center justify-center bg-black text-white">
        <div className="text-xs uppercase tracking-[0.3em] text-white/40">Active Sign Player</div>
        <div className="mt-3 text-sm text-white/60" data-testid="provision-resuming">
          Resuming previously paired screen…
        </div>
      </div>
    );
  }

  return (
    <div className="player-root flex flex-col items-center justify-center bg-black text-white">
      <div className="flex w-full max-w-3xl flex-col items-center px-8 text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-white/40">Active Sign Player</div>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-white/80">
          {state.status === 'completed' ? 'Connected' : 'Pair this screen'}
        </h1>

        <div className="mt-12 min-h-[12rem] w-full">
          {state.status === 'completed' ? (
            <ConnectedPanel previewUrl={previewUrl} />
          ) : (
            <CodePanel
              code={code}
              status={state.status}
              expiresIn={expiresIn}
            />
          )}
        </div>

        <div className="mt-12 max-w-xl text-sm leading-relaxed text-white/60">
          <Instructions status={state.status} origin={window.location.origin} />
        </div>

        {state.status !== 'completed' && (
          <div className="mt-8 flex items-center gap-4 text-xs text-white/40">
            <button
              type="button"
              className="rounded border border-white/15 px-3 py-1 hover:bg-white/5 focus:outline-none focus:ring-1 focus:ring-white/40"
              onClick={onRestart}
              data-testid="provision-new-code"
            >
              New code
            </button>
            {state.lastIssue && (
              <span className="font-mono text-amber-300/70">{state.lastIssue}</span>
            )}
          </div>
        )}

        <div className="mt-10 text-[10px] tracking-[0.2em] text-white/30">
          {fingerprintLine(state)}
        </div>
      </div>
    </div>
  );
}

function CodePanel({
  code,
  status,
  expiresIn,
}: {
  code: string;
  status: PairingState['status'];
  expiresIn: number | null;
}) {
  if (status === 'starting' || (!code && status !== 'failed')) {
    return (
      <div className="flex h-full items-center justify-center text-white/40">
        Starting up…
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="select-all font-mono text-7xl font-semibold tracking-[0.5rem] text-white sm:text-8xl"
        data-testid="provision-code"
        aria-label={`Pairing code ${code.split('').join(' ')}`}
      >
        {code || '— — — — — —'}
      </div>
      <div className="text-xs uppercase tracking-[0.3em] text-white/40">
        {status === 'claimed'
          ? 'Claimed — finalising'
          : status === 'expired'
          ? 'Code expired — generating new one'
          : status === 'cancelled'
          ? 'Cancelled — generating new one'
          : expiresIn !== null
          ? `Expires in ${formatSeconds(expiresIn)}`
          : 'Waiting for admin claim'}
      </div>
    </div>
  );
}

function ConnectedPanel({ previewUrl }: { previewUrl: string | null }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="font-mono text-3xl text-emerald-300">✓ Linked</div>
      <div className="text-xs uppercase tracking-[0.3em] text-white/40">
        Loading player…
      </div>
      {previewUrl && (
        <div
          className="mt-3 max-w-full break-all font-mono text-[10px] text-white/30"
          data-testid="provision-target-url"
        >
          {previewUrl.replace(/[?#].*/, '')}
        </div>
      )}
    </div>
  );
}

function Instructions({ status, origin }: { status: PairingState['status']; origin: string }) {
  if (status === 'completed') {
    return <span>You can leave this screen — it will reload into the player.</span>;
  }
  return (
    <span>
      Open <span className="font-mono text-white/80">{origin.replace(/^https?:\/\//, '')}</span>
      {' '}on your laptop, sign in, open a screen, and click{' '}
      <span className="font-mono text-white/80">Players → Pair Raspberry Pi Player</span>.
      Enter the code above when prompted.
    </span>
  );
}

function fingerprintLine(state: PairingState): string {
  const host = (() => {
    try {
      return window.location.hostname;
    } catch {
      return '';
    }
  })();
  const onlineHint = navigator.onLine ? 'online' : 'offline';
  const sessionAge = state.session
    ? `${Math.max(0, Math.round((Date.now() - state.session.startedAt) / 1000))}s`
    : '';
  return [host, onlineHint, sessionAge && `t+${sessionAge}`].filter(Boolean).join(' · ');
}

function formatSeconds(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function deviceLabel(): string | null {
  // The Raspberry Pi appliance image patches `?label=hostname` into the URL
  // before launching the kiosk, so a deployment can read the device name
  // off the screen. Falls back to navigator.userAgent's bracketed segment.
  try {
    const url = new URL(window.location.href);
    const fromQuery = url.searchParams.get('label');
    if (fromQuery) return fromQuery.slice(0, 80);
  } catch {
    /* ignore */
  }
  const ua = navigator.userAgent ?? '';
  const m = ua.match(/\(([^)]+)\)/);
  return m ? m[1].slice(0, 80) : null;
}

function deviceInfo(): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  out.userAgent = navigator.userAgent;
  out.language = navigator.language;
  out.platform = (navigator as { platform?: string }).platform ?? null;
  out.viewport = `${window.innerWidth}x${window.innerHeight}`;
  try {
    out.tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* ignore */
  }
  out.online = navigator.onLine;
  return out;
}
